<script setup lang="ts">
import { ref } from "vue";
import { useTranslation } from "i18next-vue";
import type { PartsTree } from "@/types/diagnostics.types";
import DRBtn from "@/components/UI/DRBtn.vue";
import PartsTreePreview from "./PartsTreePreview.vue";
import MobileConfirmModal from "./MobileConfirmModal.vue";

defineProps<{
  isExtracting: boolean;
  partsTree?: PartsTree;
  hasLinkedObservations: boolean;
  isApplying: boolean;
}>();

const emit = defineEmits<{
  (e: "apply"): void;
}>();

const { t } = useTranslation();

// Preview drawer
const showPreview = ref(false);
const showConfirmation = ref(false);

const handlePreview = () => {
  showPreview.value = true;
};

const handleApplyFromPreview = () => {
  showPreview.value = false;
  showConfirmation.value = true;
};

const handleConfirmApply = () => {
  emit("apply");
  showConfirmation.value = false;
};

// Count total parts recursively
const countParts = (parts: PartsTree["parts"]): number => {
  let count = 0;
  for (const part of parts) {
    count++;
    if (part.children?.length) {
      count += countParts(part.children);
    }
  }
  return count;
};
</script>

<template>
  <!-- Extracting Banner -->
  <div v-if="isExtracting" class="ai-banner extracting">
    <div class="banner-content">
      <div class="icon-container">
        <i class="bi-stars spinning" />
      </div>
      <div class="text-content">
        <div class="title">
          {{ t("dashboard:fieldObservations.partsTree.aiExtracting") }}
        </div>
        <div class="subtitle">
          {{ t("dashboard:fieldObservations.partsTree.aiExtractingHint") }}
        </div>
      </div>
    </div>
  </div>

  <!-- Suggestions Available Banner -->
  <div
    v-else-if="partsTree?.parts?.length"
    class="ai-banner suggestions"
    :class="{ 'view-only': hasLinkedObservations }"
  >
    <div class="banner-content">
      <div class="icon-container">
        <i class="bi-stars" />
      </div>
      <div class="text-content">
        <div class="title">
          {{
            hasLinkedObservations
              ? t("dashboard:fieldObservations.partsTree.aiSuggestionsViewOnly")
              : t("dashboard:fieldObservations.partsTree.aiSuggestionsTitle")
          }}
        </div>
        <div v-if="!hasLinkedObservations" class="subtitle">
          {{
            t("dashboard:fieldObservations.partsTree.aiSuggestionsCount", {
              count: countParts(partsTree.parts),
            })
          }}
        </div>
      </div>
      <div class="actions">
        <DRBtn
          variant="outline-white"
          size="sm"
          :label="t('dashboard:fieldObservations.partsTree.preview')"
          icon="bi-eye"
          @click="handlePreview"
        />
      </div>
    </div>
  </div>

  <!-- Preview Drawer (Mobile friendly) -->
  <Teleport to="body">
    <div
      v-if="showPreview"
      class="preview-overlay"
      @click.self="showPreview = false"
    >
      <div class="preview-drawer">
        <div class="preview-header">
          <div class="d-flex align-items-center gap-2">
            <div class="preview-icon">
              <i class="bi-stars" />
            </div>
            <div>
              <div class="preview-title">
                {{ t("dashboard:fieldObservations.partsTree.aiSuggestions") }}
              </div>
              <div class="preview-subtitle">
                {{
                  t(
                    "dashboard:fieldObservations.partsTree.aiSuggestionsCount",
                    {
                      count: partsTree ? countParts(partsTree.parts) : 0,
                    },
                  )
                }}
              </div>
            </div>
          </div>
          <button class="btn-close-preview" @click="showPreview = false">
            <i class="bi-x-lg" />
          </button>
        </div>

        <div class="preview-content">
          <PartsTreePreview
            v-if="partsTree?.parts"
            :parts="partsTree.parts"
            :level="0"
          />
        </div>

        <!-- Warning when cannot apply -->
        <div v-if="hasLinkedObservations" class="preview-warning">
          <i class="bi-info-circle" />
          <span>{{
            t("dashboard:fieldObservations.partsTree.cannotApplyMessage")
          }}</span>
        </div>

        <div class="preview-footer">
          <DRBtn
            v-if="hasLinkedObservations"
            variant="outline"
            size="sm"
            :label="t('close')"
            class="flex-grow-1"
            @click="showPreview = false"
          />
          <template v-else>
            <DRBtn
              variant="outline"
              size="sm"
              :label="t('cancel')"
              @click="showPreview = false"
            />
            <DRBtn
              variant="ia"
              size="sm"
              :label="
                t('dashboard:fieldObservations.partsTree.applySuggestions')
              "
              :loading="isApplying"
              @click="handleApplyFromPreview"
            />
          </template>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Confirmation Modal -->
  <MobileConfirmModal
    v-model="showConfirmation"
    :title="t('dashboard:fieldObservations.partsTree.applyConfirm.title')"
    :message="t('dashboard:fieldObservations.partsTree.applyConfirm.message')"
    :confirm-label="t('confirm')"
    :loading="isApplying"
    danger
    @confirm="handleConfirmApply"
  />
</template>

<style scoped lang="scss">
@import "@/style/variables.scss";

.ai-banner {
  margin: 0;
  color: white;
  overflow: hidden;

  &.extracting {
    background: linear-gradient(
      135deg,
      color-mix(in srgb, $ia, black 10%) 0%,
      $ia 100%
    );
  }

  &.suggestions {
    background: linear-gradient(135deg, #8b5cf6 0%, $ia 100%);
  }

  &.view-only {
    background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  }
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}

.icon-container {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;

  .spinning {
    animation: spin 1.5s linear infinite;
  }
}

.text-content {
  flex: 1;
  min-width: 0;
}

.title {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
}

.subtitle {
  font-size: 12px;
  opacity: 0.85;
  margin-top: 2px;
}

.actions {
  flex-shrink: 0;
}

.warning-text {
  font-size: 11px;
  opacity: 0.8;
  max-width: 120px;
  text-align: right;
  line-height: 1.3;
}

// Preview Drawer Styles
.preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.preview-drawer {
  width: 100%;
  max-height: 80vh;
  background-color: var(--bs-white);
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #8b5cf6 0%, $ia 100%);
  color: white;
}

.preview-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.preview-title {
  font-weight: 600;
  font-size: 16px;
}

.preview-subtitle {
  font-size: 12px;
  opacity: 0.85;
}

.btn-close-preview {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}

.preview-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  max-height: 50vh;
  background-color: var(--bs-white);
}

.preview-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background-color: #fff8e6;
  color: #856404;
  font-size: 13px;
  line-height: 1.4;

  i {
    font-size: 16px;
    flex-shrink: 0;
  }
}

.preview-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  border-top: 1px solid var(--bs-border-color);
  background-color: var(--bs-white);

  > * {
    flex: 1;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
